import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import { Tabs } from 'antd';
import { get, map, filter, size, slice, includes } from 'lodash';
import UIStatusTable from '../Resume/UIStatusTable';
import { Text, ExcessRow, Loading, IsMobileWrapper } from '../../common';
import QueryOrderItems from '../../graphql/getOrderItems';
import initialColumns, { substitutionColumns, splitColumns } from './OrderDetailTable';
import withReloadable from '../../../hoc/withReloadable';
import { orderProductsByStatus } from '../../../utils/misc';

const TabPane = Tabs.TabPane;

const responsiveColumns = (isMobile, columnsData) => isMobile ? [...slice(columnsData, 2, 4), ...slice(columnsData, 0, 2), ...slice(columnsData, 4)] : columnsData;

const OrderItemsDetailTab = ({ onRowClick, orderId, withPicking, inPicking, lastKnownStatus, skipQuery, dataFromParent }) => {
    const [filterKey, setFilterKey] = useState('all');

    const renderItem = (item, itemIndex) => {
        const status = get(item, 'picking.status');
        const parentProps = { onRowClick, orderId, removeOptionEnabled: !withPicking && !inPicking, parent: item, lastKnownStatus };
        const children = map(get(item, 'picking.substitution', []), (childrenItem) => ({
            ...childrenItem,
            picking: { status: String(status).toLowerCase() },
            type: String(status).toLowerCase()
        }));

        const hasChildren = size(children);

        const drawRowBorderBottom = (has, index, siz) => {
            if ((index === siz) && (has === false)) return '1px solid #eaeaea';
            if (has === 0) return '1px solid #eaeaea';
            if (has) return '1px dashed #eaeaea';
            return '1px solid transparent';
        };

        const buildRow = (tds, { width, ...item }, index, hasChildren) => (
            
            <tr key={`item-${index}`} >
                {tds.map(({ dataIndex, render = this.renderCell }, key) => (
                    <td
                        key={key}
                        style={{
                            borderBottom: drawRowBorderBottom(hasChildren, index + 1, size(children)),
                            padding: 10,
                            fontSize: '11px',
                            maxWidth: '210px',
                            textAlign: [7, 8, 9].includes(key) ? 'right' : 'left'
                        }}
                    >
                        { render({ ...item }, dataIndex, parentProps, lastKnownStatus) }
                    </td>
                ))}
            </tr>
        );

        const excess = get(item, 'picking.excess', 0);
        const excessColumns = ['', '', '', '', '', '', '', '', excess, ''];

        const parentRow = [item].map((item, index) => (
            <IsMobileWrapper key={index}>
                {({ isMobile }) => (
                    <Fragment>
                        { buildRow(responsiveColumns(isMobile, filterKey === 'split' ? splitColumns : initialColumns), item, index, hasChildren) }
                        { excess !== 0 && !hasChildren && filterKey !== 'split' && <ExcessRow excess={excess} columns={excessColumns} /> }
                    </Fragment>
                )}
            </IsMobileWrapper>
        ));

        return hasChildren && filterKey !== 'split' ? (
            <IsMobileWrapper key={itemIndex}>
                {({ isMobile }) => (
                    <Fragment>
                        { [...parentRow, children.map((item, index) => buildRow(responsiveColumns(isMobile, substitutionColumns), item, index, false))] }
                        <ExcessRow excess={excess} columns={excessColumns} />
                    </Fragment>
                )}
            </IsMobileWrapper>
        ) : parentRow;
    };

    const renderHeader = ({ title, width }, key) => (
        <th
            className=''
            key={`th-${key}`}
            style={{
                width,
                fontSize: '12px',
                textAlign: [7, 8, 9].includes(key) ? 'right' : 'left',
                verticalAlign: 'top',
                zIndex: key === 0 ? 2 : 1
            }}
        >
            {title}
        </th>
    );

    const getHeaderLabels = (isMobile, isSplit) => {
        const columns = (filterKey === 'split' && isSplit) ? splitColumns : initialColumns;
        return responsiveColumns(isMobile, columns).map(({ title, width }) => ({ title, width }));
    };

    const onChangeTab = (key) => setFilterKey(key);

    const filterItems = (data) => {
        const items = [...get(data, 'items', []), ...get(data, 'removedItems', [])];

        const currentFilterKey = {
            'picked': ['PICKED', 'PARTIAL', 'PARTIAL_ACCEPTED', 'PICK-VALIDATE'],
            'substitute': ['FREE_PICKING', 'FREE_PICKING_ACCEPTED', 'REPLACE', 'SUBSTITUTE_CONFIRMED', 'REPICKING_SUBSTITUTE_CONFIRMED'],
            'no-stock': ['OUT_STOCK', 'OUT_STOCK_ACCEPTED', 'REPICKING_OUT_STOCK_ACCEPTED', 'UNPICK_BY_STORE', 'UNPICK_ADDED_BY_CLIENT'],
            'added': ['ADDED_BY_CLIENT']
        }[filterKey] || [];

        if (!filterKey || filterKey === 'all') {
            const toCompleteReducer = () => {
                if (get(lastKnownStatus, 'code') === '7') {
                    const unpicked = filter(items, item => !get(item, 'status', get(item, 'picking.status')));
                    // const picked = filter(data, item => get(item, 'status', get(item, 'picking.status')));
                    return unpicked;
                }
                return [];
            };
            return withPicking ? [...toCompleteReducer(), ...orderProductsByStatus(items)] : items;
        }

        if (get(data, 'split') && filterKey === 'split') {
            const eanSplitList = get(data, 'eanSplitList');
            const nameSpliter = get(data, 'nameSpliter');
            const itemsFiltered = filter(items, ({ ean }) => includes(eanSplitList, ean));

            return map(itemsFiltered, (item) => ({ ...item, nameSpliter, lastKnownStatus }));
        } else {
            return filter(items, (item) => currentFilterKey.includes(get(item, 'status', get(item, 'picking.status'))));
        }
    };

    const commonTableContainerStyles = {
        margin: '0 28px',
        display: 'flex',
        flexGrow: '1',
        alignItems: 'flex-start',
        justifyContent: 'center',
        overflowX: 'auto'
    };

    const tableTabs = [
        {
            key: 'all',
            text: 'order_detail.table.all_items'
        },
        {
            key: 'picked',
            text: 'order_detail.table.picked_items'
        },
        {
            key: 'substitute',
            text: 'order_detail.table.replaced_items'
        },
        {
            key: 'no-stock',
            text: 'order_detail.table.no_stock_items'
        },
        {
            key: 'added',
            text: 'Agregados'
        },
        {
            key: 'split',
            text: 'Split order'
        }
    ];

    const renderTabs = (isSplit) => {
        return <Tabs defaultActiveKey='all' onChange={onChangeTab}>
            { map(filter(tableTabs, ({ key }) => {
                return isSplit ? true : key !== 'split';
            }), ({ key, text }) => (
                <TabPane key={key} tab={<Text strong size={13} color={tabColor(key)} text={text} />} />
            )) }
        </Tabs>;
    };

    const tabColor = key => key === filterKey ? '#333333' : '#c0c0c0';

    const renderUIStatusTable = (data, isMobile, loading) => {
        const isSplit = get(data, 'split');

        return <>
            {renderTabs(isSplit)}
            <div style={commonTableContainerStyles}>
                <Loading loading={loading}>
                    <UIStatusTable
                        isMobile={isMobile}
                        className='default-table'
                        tableProps={{ className: 'order-items-table' }}
                        renderItem={(item, itemIndex) => renderItem(item, itemIndex)}
                        renderHeader={renderHeader}
                        headers={getHeaderLabels(isMobile, isSplit)}
                        data={filterItems(data)}
                    />
                </Loading>
            </div>;
        </>;
    };

    return (
        <>
            <IsMobileWrapper>
                {({ isMobile }) => {
                    return skipQuery
                        ? renderUIStatusTable(dataFromParent)
                        : <QueryOrderItems variables={{ id: orderId }} withPicking={withPicking}>
                            {({ data, loading }) => {
                                return renderUIStatusTable(data, isMobile, loading);
                            }}
                        </QueryOrderItems>;
                }}
            </IsMobileWrapper>
        </>
    );
};

OrderItemsDetailTab.propTypes = {
    orderId: PropTypes.string,
    withPicking: PropTypes.bool,
    inPicking: PropTypes.bool,
    onRowClick: PropTypes.func,
    skipQuery: PropTypes.bool,
    dataFromParent: PropTypes.object,
    lastKnownStatus: PropTypes.string
};

export default withReloadable(OrderItemsDetailTab, ['ts']);
